import React, { useEffect, useState, useCallback, memo } from "react";
import { CreateKeyModal } from "./CreateKeyModal";
import { ApiKeysTable } from "./ApiKeysTable";
import { Footer } from "./Footer";
import { useApiKeys } from "../hooks/useApiKeys";
import { useUsage } from "../hooks/useUsage";
import { ErrorBoundary } from "./ErrorBoundary";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, Key } from "lucide-react";
import { CommandBanner } from "./CommandBanner";
import { useSubscription } from "@/hooks/useSubscription";
import { SubscriptionCard } from "./billing/SubscriptionCard";
import { Link } from "react-router-dom";
import { SiteHeader } from "./SiteHeader";

const DashboardComponent: React.FC = () => {
  const subscriptionInfo = useSubscription();
  const { usage, isLoading: usageLoading } = useUsage();
  const [isCreateKeyModalOpen, setIsCreateKeyModalOpen] = useState(false);
  const { apiKeys, isLoading, error, fetchKeys, createKey, deleteKey } =
    useApiKeys();

  useEffect(() => {
    fetchKeys();
  }, []);

  const handleCreateKey = useCallback(() => {
    setIsCreateKeyModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsCreateKeyModalOpen(false);
  }, []);

  const handleSubmitKey = useCallback(
    async (name: string) => {
      const result = await createKey(name);
      return { success: result.success, key: result.key };
    },
    [createKey]
  );

  const hasApiKeys = apiKeys.length > 0;

  return (
    <>
      <div className="flex flex-col min-h-screen">
        <SiteHeader className="sticky top-0 z-10" />
        {/* Enhanced background with optimized height */}
        <div className="relative flex flex-col flex-1 bg-gradient-to-br from-slate-50 via-white to-slate-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950/50">
          {/* Refined background pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:16px_16px]" />

          <main className="relative flex flex-col flex-1 overflow-auto">
            <div className="w-full px-6 pt-4 pb-6 mx-auto space-y-6 max-w-7xl">
              {/* Compact Page Header with Apple-style typography */}
              <section className="space-y-2 duration-500 ease-out animate-in fade-in-0">
                {/* Just for flex so that Both CommandBanner and Card come side by side */}
                <div className="flex flex-col w-full min-w-0 gap-6 lg:flex-row">
                  {/* Command Banner */}
                  <CommandBanner className="flex-1 min-w-[300px] w-full duration-700 ease-out animate-in fade-in-0" />

                  {/* Compact Quick Stats with Apple-style design */}
                  {
                    <div className="flex-shrink-0 w-full lg:max-w-sm">
                      <div className="h-full gap-4 duration-700 ease-out md:grid-cols-3 animate-in fade-in-0">
                        <Link to="/billing">
                          <SubscriptionCard
                            planName={subscriptionInfo.planName}
                            price={subscriptionInfo.price}
                            period={subscriptionInfo.period}
                            hasActivePlan={subscriptionInfo.hasActivePlan}
                            isLoading={!subscriptionInfo.isLoaded}
                            usage={usage}
                            usageLoading={usageLoading}
                          />
                        </Link>
                      </div>
                    </div>
                  }
                </div>
              </section>

              {/* Compact Content Grid */}
              <section className="flex-1 gap-6 duration-700 ease-out lg:grid-cols-3 animate-in fade-in-0">
                <div className="space-y-4 lg:col-span-2">
                  <Card className="transition-all duration-300 ease-out border-0 shadow-xl bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-slate-900/5 dark:shadow-slate-900/20 hover:shadow-2xl hover:shadow-slate-900/10 dark:hover:shadow-slate-900/30">
                    <CardHeader className="flex px-6 pt-6 pb-4 justify-between">
                      <div className="flex flex-col gap-2">
                        <CardTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight">
                          <Key className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                          {hasApiKeys ? "Your API Keys" : "API Keys"}
                        </CardTitle>
                        <CardDescription className="text-sm text-slate-600 dark:text-slate-400">
                          {hasApiKeys
                            ? "Manage your API keys and monitor their status."
                            : "Create your first API key to get started with the Forge API."}
                        </CardDescription>
                      </div>
                      <div className="flex-shrink-0 w-full sm:w-auto">
                        <Button
                          onClick={handleCreateKey}
                          className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-slate-200 dark:text-slate-900 font-medium px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-slate-900/25 hover:shadow-xl hover:shadow-slate-900/30 whitespace-nowrap text-sm sm:text-base"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          <span className="hidden sm:inline">
                            Create API Key
                          </span>
                          <span className="sm:hidden">Create Key</span>
                        </Button>
                      </div>
                    </CardHeader>

                    <CardContent className="p-2">
                      <ErrorBoundary>
                        <ApiKeysTable
                          apiKeys={apiKeys}
                          isLoading={isLoading}
                          error={error}
                          onDelete={deleteKey}
                        />
                      </ErrorBoundary>
                    </CardContent>
                  </Card>
                </div>
              </section>
            </div>
          </main>
          <Footer />
        </div>

        <CreateKeyModal
          isOpen={isCreateKeyModalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitKey}
        />
      </div>
    </>
  );
};

export const Dashboard = memo(DashboardComponent);
